import React, { useEffect, useState, useCallback } from "react";
import { BadgeCheck } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { ApprovalBadge, EmptyState } from "@/components/Bits";
import TaskDrawer from "@/components/TaskDrawer";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = useCallback(() => api.get("/approvals").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const decide = async (e, id, decision) => {
    e.stopPropagation();
    await api.post(`/approvals/${id}?decision=${encodeURIComponent(decision)}`);
    toast.success(`Marked ${decision}`);
    load();
  };

  const pending = items.filter((i) => i.approval_status !== "Approved");

  return (
    <div>
      <PageHeader title="Approval Queue" subtitle={`${pending.length} items awaiting decision`} />
      <div className="p-8">
        {items.length === 0 ? (
          <EmptyState icon={BadgeCheck} title="Approval queue is clear" hint="Nothing needs your sign-off right now." />
        ) : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-white/10 bg-[#0a0a0a]">
                    {["ID", "Brand", "Item", "Division", "Submitted By", "Status", "Action"].map((h) => (
                      <th key={h} className="py-3 px-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((t) => (
                    <tr key={t.id} data-testid={`approval-row-${t.id}`} onClick={() => { setDrawerId(t.id); setDrawerOpen(true); }} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                      <td className="py-3 px-3 font-mono text-xs text-zinc-500">{t.ref}</td>
                      <td className="py-3 px-3 text-zinc-200">{t.brand}</td>
                      <td className="py-3 px-3 text-zinc-300 max-w-[220px] truncate">{t.title}</td>
                      <td className="py-3 px-3 text-zinc-400 text-xs">{t.division}</td>
                      <td className="py-3 px-3 text-zinc-400">{t.assigned_to || "—"}</td>
                      <td className="py-3 px-3"><ApprovalBadge status={t.approval_status} /></td>
                      <td className="py-3 px-3">
                        {t.approval_status !== "Approved" ? (
                          <div className="flex gap-1.5">
                            <button data-testid={`approve-${t.id}`} onClick={(e) => decide(e, t.id, "Approved")} className="text-[10px] font-mono uppercase tracking-wider bg-[#00FF66]/10 border border-[#00FF66]/30 text-[#00FF66] px-2 py-1 rounded-sm hover:bg-[#00FF66]/20 transition-colors">Approve</button>
                            <button data-testid={`revise-${t.id}`} onClick={(e) => decide(e, t.id, "Revision Requested")} className="text-[10px] font-mono uppercase tracking-wider bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF6259] px-2 py-1 rounded-sm hover:bg-[#FF3B30]/20 transition-colors">Revise</button>
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-600">Done</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <TaskDrawer taskId={drawerId} open={drawerOpen} onOpenChange={setDrawerOpen} onChanged={load} />
    </div>
  );
}
